/**
 * @fileoverview Hook Data Layer para obtener los demás chats del microservicio que
 * tiene el customer de la conversación seleccionada, en otras plataformas o variantes
 * de plataforma (WhatsApp BSP / QR / Gupshup, Messenger, Instagram, TikTok).
 * @module useCustomerChannelsData
 * @category Hook - Data Layer
 * @since 3.2.0
 * @version 3.2.0
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import ConversationModelMC from '../../../models/inbox/ConversationModelMC';
import useSelectedConversation from '../../shared/useSelectedConversation';
import { selectCustomerChannelChats } from '../../../../services/inbox/customerChannelsService';
import {
  isInboxMsChat,
  toSidebarPlatformKey
} from '../../../../services/inbox/inboxMsChatPlatform';
import PLATFORM_CONFIG from '../../../utils/platformConfig';

const CHATS_PER_PAGE = 50;

/**
 * Convierte un item crudo de la API del MS en la forma que consume el selector.
 * Conserva el item original en `raw` porque es lo que se guarda en el slot de Redux.
 *
 * @param {Object} chat - Item `{ id, type, attributes }` de la API.
 * @returns {Object} Canal listo para pintar.
 */
function toChannel(chat, currentChatId) {
  const attributes = chat.attributes || {};
  const { platform } = attributes;
  const platformConfig = PLATFORM_CONFIG[platform] || {};
  // El id canónico del JSON:API vive en la raíz; algunos payloads también lo
  // duplican en attributes — hay que aceptar ambos para marcar el canal actual.
  const chatId = chat.id ?? attributes.id;

  return {
    raw: chat,
    id: chatId,
    isCurrent: currentChatId != null && String(chatId) === String(currentChatId),
    platform,
    sidebarPlatform: toSidebarPlatformKey(platform),
    platformName: platformConfig.name || platform,
    platformIcon: platformConfig.src || null,
    accountName: attributes.platform_retailer_name || null,
    customerId: attributes.customer_id,
    lastMessageAt: attributes.recent_message_date || null,
    isUnRead: !!attributes.has_unread_messages,
    unreadCount: attributes.unread_messages_count || 0
  };
}

/**
 * Hook Data Layer con los demás canales del customer actual.
 *
 * - Solo aplica a chats del microservicio de inbox: si la plataforma actual no está
 *   habilitada en el MS o el chat todavía no tiene `customerId` (el round-trip de
 *   creación del customer por Kafka no volvió), devuelve una lista vacía sin pedir nada.
 * - Cachea por `customerId` mientras el hook esté montado (pinta al toque al volver
 *   al mismo contacto) y revalida en cada apertura para no quedarse con hermanos viejos.
 *
 * @function useCustomerChannelsData
 * @returns {{ channels: Array<Object>, loading: boolean }}
 *
 * @see selectCustomerChannelChats Reglas de filtrado
 * @see CustomerChannelsSelectorMC Componente de presentación
 * @since 3.2.0
 */
export default function useCustomerChannelsData() {
  const conversation = useSelectedConversation();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef({});

  const customerId = conversation?.customerId;
  const currentChatId = conversation?.id;
  // Mismo discriminador que el filtrado de hermanos. El flag de WhatsApp cubre las
  // tres variantes (BSP / QR / Gupshup) vía MS_PLATFORM_TO_ROLLOUT_FLAG, así que al
  // saltar entre canales de un retailer migrado el selector se sigue mostrando.
  const enabled = isInboxMsChat(conversation?.platform) && !!customerId;

  // Sin cancelación, una respuesta lenta del customer anterior podía pisar el estado
  // del actual y dejar el selector oculto (p. ej. cache/respuesta con un solo chat).
  // Con cache se pinta al toque y se revalida para no quedarse con hermanos viejos.
  useEffect(() => {
    if (!enabled) {
      setChats([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const cacheKey = String(customerId);
    const cached = cacheRef.current[cacheKey];

    if (cached) {
      setChats(cached);
      setLoading(false);
    } else {
      setChats([]);
      setLoading(true);
    }

    ConversationModelMC.getMicroserviceCustomerChats({
      customerId,
      perPage: CHATS_PER_PAGE
    })
      .then((response) => {
        if (cancelled) return;
        const data = response?.data || [];
        cacheRef.current[cacheKey] = data;
        setChats(data);
      })
      .catch(() => {
        // El selector es accesorio: si falla y no hay cache, simplemente no se ofrece.
        if (cancelled || cached) return;
        setChats([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, customerId]);

  const channels = useMemo(() => {
    if (!enabled) return [];
    return selectCustomerChannelChats(chats).map((chat) => toChannel(chat, currentChatId));
  }, [enabled, chats, currentChatId]);

  return { channels, loading: enabled && loading };
}
